import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { DOCS_CODE } from '../docs-code-snippets';

export const DocsPage: React.FC = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', paddingBottom: '96px', backgroundColor: '#121212' }}>
      
      {/* Docs Header Banner */}
      <header className="container" style={{ padding: '0 20px 48px', maxWidth: '1200px' }}>
        <div className="eyebrow" style={{ color: '#6D7CFF', marginBottom: '12px' }}>
          Developer Reference
        </div>
        <h1 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-1.2px',
            color: '#FFFFFF',
            margin: '0 0 16px'
          }}
        >
          Draway Auth Documentation
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '16px', color: '#D7D7D7', lineHeight: 1.6, maxWidth: '800px', margin: 0 }}>
          Complete integration guides, cryptographic specifications, multi-channel verification flows, and exhaustive REST API references for enterprise SaaS engineering teams.
        </p>
      </header>

      {/* Docs Body Layout */}
      <div className="container docs-layout" style={{ maxWidth: '1200px', padding: '0 20px' }}>
        
        {/* Sticky Sidebar */}
        <aside className="docs-sidebar">
          <div>
            <div className="docs-sidebar-group-title">Getting Started</div>
            <ul className="docs-sidebar-links">
              <li><a href="#quickstart" className="docs-sidebar-link">Quickstart &amp; Setup</a></li>
              <li><a href="#lifecycle" className="docs-sidebar-link">Authentication Lifecycle</a></li>
            </ul>
          </div>

          <div>
            <div className="docs-sidebar-group-title">Verification &amp; MFA</div>
            <ul className="docs-sidebar-links">
              <li><a href="#passkeys" className="docs-sidebar-link">WebAuthn Passkeys</a></li>
              <li><a href="#whatsapp" className="docs-sidebar-link">WhatsApp &amp; SMS OTP</a></li>
              <li><a href="#toll-fraud" className="docs-sidebar-link">SMS Toll-Fraud Shield</a></li>
            </ul>
          </div>

          <div>
            <div className="docs-sidebar-group-title">Multi-Tenancy &amp; B2B</div>
            <ul className="docs-sidebar-links">
              <li><a href="#tenancy" className="docs-sidebar-link">Tenant Custom Domains</a></li>
              <li><a href="#shadow-migration" className="docs-sidebar-link">Shadow Migration</a></li>
              <li><a href="#api-keys" className="docs-sidebar-link">M2M Developer API Keys</a></li>
            </ul>
          </div>

          <div>
            <div className="docs-sidebar-group-title">Security Architecture</div>
            <ul className="docs-sidebar-links">
              <li><a href="#cryptography" className="docs-sidebar-link">Argon2id + AES Vault</a></li>
              <li><a href="#edge-jwks" className="docs-sidebar-link">Edge Local JWKS</a></li>
              <li><a href="#impersonation" className="docs-sidebar-link">Support Impersonation</a></li>
            </ul>
          </div>

          <div>
            <div className="docs-sidebar-group-title">API Reference</div>
            <ul className="docs-sidebar-links">
              <li><a href="#api-auth" className="docs-sidebar-link">/v1/auth Routes</a></li>
              <li><a href="#api-mfa" className="docs-sidebar-link">/v1/mfa Routes</a></li>
              <li><a href="#api-clients" className="docs-sidebar-link">/v1/clients Routes</a></li>
              <li><a href="#api-admin" className="docs-sidebar-link">/v1/admin Routes</a></li>
              <li><a href="#api-keys-ref" className="docs-sidebar-link">/v1/api-keys Routes</a></li>
              <li><a href="#api-migrate-ref" className="docs-sidebar-link">/v1/migrate Routes</a></li>
            </ul>
          </div>
        </aside>

        {/* Main Documentation Content */}
        <main className="docs-content">
          
          {/* Section: Quickstart */}
          <section id="quickstart" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Quickstart &amp; Installation
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Draway Auth is distributed as a zero-config, framework-agnostic TypeScript client library. It automatically handles cookie encryption, token refreshing, and WebAuthn challenges.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px', marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>TERMINAL</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.install, 'install')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'install' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'install' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.install }} />
              </pre>
            </div>

            <div className="docs-callout">
              <strong style={{ color: '#FFFFFF' }}>Environment Keys:</strong> To start making requests, generate your client API key (<code>ak_live_...</code>) from the Draway Admin console or VIP waitlist.
            </div>

            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '28px 0 12px' }}>
              Initialization
            </h3>
            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.init }} />
              </pre>
            </div>
          </section>

          {/* Section: Authentication Lifecycle */}
          <section id="lifecycle" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              The 2-Phase Authentication Flow
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 20px' }}>
              Unlike traditional setups that expose user tokens prematurely, Draway Auth uses a cryptographically protected two-phase authentication state machine.
            </p>

            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '20px 0 12px' }}>
              Phase 1: Primary Login
            </h3>
            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.phase1 }} />
              </pre>
            </div>

            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '28px 0 12px' }}>
              Phase 2: MFA Verification
            </h3>
            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.phase2 }} />
              </pre>
            </div>
          </section>

          {/* Section: WebAuthn Passkeys */}
          <section id="passkeys" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              FIDO2 WebAuthn Passkeys
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Native biometric authentication using Apple TouchID, FaceID, Windows Hello, and YubiKeys. Zero passwords are ever stored or transmitted across the wire.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.passkey }} />
              </pre>
            </div>
          </section>

          {/* Section: Multi-Channel Messaging & WhatsApp */}
          <section id="whatsapp" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Multi-Channel Delivery &amp; WhatsApp Auth
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Draway Auth routes verification codes across four independent messaging channels with automatic user fallbacks:
            </p>

            <ul style={{ paddingLeft: '20px', margin: '16px 0', lineHeight: 1.8, color: '#D7D7D7', fontSize: '14px' }}>
              <li><strong style={{ color: '#FFFFFF' }}>WhatsApp OTP:</strong> Direct, end-to-end encrypted dispatch with 98%+ delivery rates bypassing telecom spam blockers (₹0.99 per message).</li>
              <li><strong style={{ color: '#FFFFFF' }}>SMS OTP:</strong> High-priority carrier routes with full DLT regulatory compliance (100 free SMS included every month, then ₹0.99/SMS).</li>
              <li><strong style={{ color: '#FFFFFF' }}>Email OTP:</strong> HTML-formatted responsive OTPs, magic codes, and security anomaly alerts (unlimited on all plans).</li>
              <li><strong style={{ color: '#FFFFFF' }}>Authenticator Apps (TOTP):</strong> Offline RFC 6238 generation via Google Authenticator, 1Password, or Authy.</li>
            </ul>
          </section>

          {/* Section: Multi-Tenancy & Custom Domains */}
          <section id="tenancy" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Multi-Tenant Custom Domains
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Every enterprise client organization can use their own custom subdomain (e.g. <code>auth.customer.com</code>).
            </p>

            <div className="docs-callout">
              <strong style={{ color: '#FFFFFF' }}>DNS Verification Protocol:</strong> When provisioning a custom domain, Draway generates a cryptographically signed token (<code>draway-verify-94a2b...</code>). Have your customer add a <code>TXT</code> record to their DNS root pointing to this token, then trigger verification:
            </div>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.dns }} />
              </pre>
            </div>
          </section>

          {/* Section: Cryptographic Vault */}
          <section id="cryptography" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Cryptographic Vault &amp; Defense-in-Depth
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 20px' }}>
              Draway Auth treats database compromise as a reality, enforcing dual-layer cryptographic protection at rest:
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '16px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 8px' }}>
                  Argon2id Hashing
                </h4>
                <p style={{ fontSize: '13px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                  Memory-hard key derivation protecting passwords and single-use recovery codes against ASIC and GPU cluster brute-forcing.
                </p>
              </div>
              <div style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '16px', fontWeight: 500, color: '#FFFFFF', margin: '0 0 8px' }}>
                  AES-256-GCM Encryption
                </h4>
                <p style={{ fontSize: '13px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
                  MFA seeds stored as authenticated <code>iv:authTag:ciphertext</code>. Prevents tampering, bit-flipping, and plaintext exposure.
                </p>
              </div>
            </div>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.crypto }} />
              </pre>
            </div>

            <div className="docs-callout docs-callout-warning" style={{ marginTop: '20px' }}>
              <strong style={{ color: '#FFFFFF' }}>Automatic 15-Minute Lockout:</strong> The engine tracks consecutive failed MFA attempts on the partial session. After 5 failures, the server returns <code>403 Forbidden</code> with an automated 15-minute freeze timestamp (<code>lockedUntil</code>).
            </div>
          </section>

          {/* Section: SMS Toll-Fraud Shield */}
          <section id="toll-fraud" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              SMS Toll-Fraud Shield &amp; Auto-Circuit Breaker
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Toll fraud (telephony pumping) occurs when botnets hammer public OTP endpoints to premium carrier destinations. Draway Auth protects you with real-time risk scoring, device fingerprinting, and automatic circuit breaking.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>CONFIG &bull; SHIELD_RULES.TS</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.tollFraud, 'tollFraud')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'tollFraud' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'tollFraud' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.tollFraud }} />
              </pre>
            </div>

            <div className="docs-callout" style={{ marginTop: '20px' }}>
              <strong style={{ color: '#FFFFFF' }}>WhatsApp OTP Fallback:</strong> If the OTP failure ratio exceeds 20%, Draway automatically routes legitimate requests through WhatsApp Business API (98% delivery rate) instead of costly SMS gateways.
            </div>
          </section>

          {/* Section: Dual-Write Shadow Migration */}
          <section id="shadow-migration" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Dual-Write Shadow Migration (Zero Password Resets)
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Migrate seamlessly from Auth0, Clerk, Cognito, or Firebase without forcing your users to reset passwords. Draway's shadow migration proxy validates legacy hashes on user login and silently upgrades them to Argon2id in your Draway vault.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>TYPESCRIPT &bull; SHADOW_PROXY.TS</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.shadowMigration, 'shadowMigration')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'shadowMigration' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'shadowMigration' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.shadowMigration }} />
              </pre>
            </div>
          </section>

          {/* Section: Developer M2M API Keys */}
          <section id="api-keys" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Developer M2M API Keys Vault
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Issue Stripe-style API keys (<code>dw_live_...</code>) for customer developers. Draway stores only the SHA-256 hash, enforces granular permission scopes, and provides per-key rate limiting.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>NODE.JS &bull; API_KEYS.TS</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.apiKeys, 'apiKeys')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'apiKeys' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'apiKeys' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.apiKeys }} />
              </pre>
            </div>
          </section>

          {/* Section: Edge Local JWKS Verification */}
          <section id="edge-jwks" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Edge Local JWKS Verification (&lt;1ms Latency)
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Never let an auth provider outage take down your application. Verify stateless JWT tokens locally at the edge using cached public keys from <code>/.well-known/jwks.json</code>.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>CLOUDFLARE WORKERS / EDGE &bull; VERIFY.TS</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.edgeJwks, 'edgeJwks')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'edgeJwks' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'edgeJwks' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.edgeJwks }} />
              </pre>
            </div>
          </section>

          {/* Section: Support Impersonation */}
          <section id="impersonation" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              Cryptographic User Impersonation (Support Mode)
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 16px' }}>
              Enable your support team to reproduce customer bugs safely. Generates a time-bounded (15m) cryptographic token with read-only restrictions and immutable tamper-proof audit trails.
            </p>

            <div className="code-block" style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: '#6D7CFF', letterSpacing: '0.05em' }}>TYPESCRIPT &bull; IMPERSONATION.TS</span>
                <button 
                  type="button" 
                  className="sdk-copy-btn"
                  onClick={() => copyCode(DOCS_CODE.impersonation, 'impersonation')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', cursor: 'pointer' }}
                >
                  {copiedId === 'impersonation' ? <Check size={12} color="#10B981" /> : <Copy size={12} />}
                  {copiedId === 'impersonation' ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <pre style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', lineHeight: 1.6, color: '#D7D7D7' }}>
                <code dangerouslySetInnerHTML={{ __html: DOCS_CODE.impersonation }} />
              </pre>
            </div>
          </section>

          {/* Section: REST API Reference Table */}
          <section id="api-auth" className="docs-section">
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '26px', fontWeight: 400, color: '#FFFFFF', margin: '0 0 12px' }}>
              REST API Reference
            </h2>
            <p style={{ fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: '0 0 24px' }}>
              All API requests require an <code>x-api-key</code> header or must be sent from an authorized custom domain.
            </p>

            {/* /v1/auth Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '28px 0 12px' }}>
              1. Authentication Endpoints (/v1/auth)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/register</td>
                    <td>Create a new user account in tenant client</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/login</td>
                    <td>Primary login (returns mfaToken if MFA enabled)</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/verify-login</td>
                    <td>Verify 2FA code (WhatsApp, TOTP, SMS, Email, Recovery)</td>
                    <td>Client + MFA Token</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/request-mfa-code</td>
                    <td>Dispatch new WhatsApp, SMS, or Email code</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/refresh-tokens</td>
                    <td>Exchange refresh token family for new access token</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-get">GET</span></td>
                    <td>/v1/auth/me</td>
                    <td>Fetch current authenticated user profile</td>
                    <td>User Session Cookie</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-patch">PATCH</span></td>
                    <td>/v1/auth/update-password</td>
                    <td>Change password for active user session</td>
                    <td>User Session Cookie</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/forgot-password</td>
                    <td>Trigger password reset token dispatch</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/auth/reset-password</td>
                    <td>Reset user password with email/SMS token</td>
                    <td>Client API Key</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* /v1/mfa Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '36px 0 12px' }} id="api-mfa">
              2. MFA &amp; Biometrics Endpoints (/v1/mfa)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/setup</td>
                    <td>Initialize TOTP QR code &amp; Base32 secret generation</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/verify</td>
                    <td>Verify setup code and activate MFA for user</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/preference</td>
                    <td>Set default channel (whatsapp, totp, sms, passkey)</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-get">GET</span></td>
                    <td>/v1/mfa/recovery-codes</td>
                    <td>View single-use emergency recovery codes</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passkey/register/options</td>
                    <td>Generate WebAuthn challenge for new passkey</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passkey/register/verify</td>
                    <td>Verify and register hardware biometric credential</td>
                    <td>User Session</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passkey/login/options</td>
                    <td>Get WebAuthn assertion options for passwordless login</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passkey/login/verify</td>
                    <td>Verify biometric assertion and issue active session</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passwordless/email/request</td>
                    <td>Request instant passwordless OTP via email</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/mfa/passwordless/email/verify</td>
                    <td>Verify OTP and authenticate without password</td>
                    <td>None</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* /v1/clients Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '36px 0 12px' }} id="api-clients">
              3. Client &amp; Tenant Management (/v1/clients)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-get">GET</span></td>
                    <td>/v1/clients/me</td>
                    <td>Get current client settings and verified origins</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-patch">PATCH</span></td>
                    <td>/v1/clients/me</td>
                    <td>Update allowed CORS origins, session TTL, MFA mandate</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/clients/verify-domain</td>
                    <td>Verify custom domain DNS TXT record token</td>
                    <td>Client API Key</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* /v1/admin Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '36px 0 12px' }} id="api-admin">
              4. Platform Administration (/v1/admin)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/admin/login</td>
                    <td>Authenticate platform administrator</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-get">GET</span></td>
                    <td>/v1/admin/stats</td>
                    <td>Cluster-wide telemetry, user count, active sessions</td>
                    <td>Super Admin</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/admin/tenants</td>
                    <td>Provision a new tenant client organization</td>
                    <td>Super Admin</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/admin/test-service</td>
                    <td>Probe live deliverability: Email, SMS, WhatsApp, DB</td>
                    <td>Super Admin</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* /v1/api-keys Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '36px 0 12px' }} id="api-keys-ref">
              5. Developer API Keys Vault (/v1/api-keys)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/api-keys</td>
                    <td>Generate new scoped API key (dw_live_...)</td>
                    <td>Client Admin Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-get">GET</span></td>
                    <td>/v1/api-keys</td>
                    <td>List active API keys with usage stats &amp; prefixes</td>
                    <td>Client Admin Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-delete">DELETE</span></td>
                    <td>/v1/api-keys/:keyId</td>
                    <td>Immediately revoke API key across all edge nodes</td>
                    <td>Client Admin Key</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* /v1/migrate Table */}
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '18px', fontWeight: 500, color: '#FFFFFF', margin: '36px 0 12px' }} id="api-migrate-ref">
              6. Migration &amp; Impersonation (/v1/migrate)
            </h3>
            <div style={{ overflowX: 'auto', backgroundColor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
              <table className="api-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                    <th>Auth Required</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/migrate/shadow-auth</td>
                    <td>Verify legacy hash &amp; silently upgrade to Argon2id</td>
                    <td>Client API Key</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/migrate/export-hashes</td>
                    <td>Export encrypted user vault for backup/portability</td>
                    <td>Master Secret</td>
                  </tr>
                  <tr>
                    <td><span className="method-badge method-post">POST</span></td>
                    <td>/v1/admin/impersonate</td>
                    <td>Generate time-boxed (15m) support session token</td>
                    <td>Super Admin</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </section>
        </main>
      </div>

    </div>
  );
};
